import { SuperSearchFirmResult, SuperSearchPageResult, SuperSearchResult } from 'api';
import {
  firmAuditLogsUrl,
  firmCompGroupsUrl,
  firmScimConfigurationUrl,
  firmSettingsUrl,
  firmSingleSignOnUrl,
  firmUserManagementUrl,
  firmValuationsUrl,
  userProfileUrl,
} from 'common/config/urls';
import {
  BULK_ACTIONS,
  EXCEL_EXPORT,
  FIRM_ACTION_QUERY_PARAM,
  FIRM_PAGE_ACTIONS,
  type FirmPageAction,
  PDF_EXPORT,
} from 'common/constants/pageActions';
import type { GuidedTourRegistryItem } from 'components/ui';
import { VALUATIONS_DEFAULT_TERTIARY_NAVIGATION_SLUG } from 'pages/FirmValuations/firmValuationsNavigation.constants';
import { SUPER_SEARCH_COMMANDS } from './SuperSearch.constants';
import type { SuperSearchCommand } from './SuperSearch.constants';
import type {
  SuperSearchContext,
  SuperSearchDisplayItem,
  SuperSearchSection,
} from './SuperSearch.types';
import { matchesSuperSearchQuery } from './SuperSearch.utils';

interface FirmCommandPermissions {
  canAccessAuditLogs: boolean;
  canAccessFirmSettings: boolean;
  canAccessUserManagement: boolean;
  canCreateCompany: boolean;
  canCreateMeasurementDate: boolean;
  canExportExcel: boolean;
  canExportPdf: boolean;
  canRunBulkActions: boolean;
}

interface FirmCommandDefinition {
  command?: SuperSearchCommand;
  contextLabel?: string;
  key: string;
  label: string;
  path?: string;
  tourId?: string;
  typeLabel?: string;
}

const buildFirmActionPath = (firmSlug: string, action: FirmPageAction) => {
  const searchParams = new URLSearchParams({ [FIRM_ACTION_QUERY_PARAM]: action });
  return `${firmValuationsUrl(firmSlug)}/${VALUATIONS_DEFAULT_TERTIARY_NAVIGATION_SLUG}?${searchParams.toString()}`;
};

const getCommandKind = (command?: SuperSearchCommand): SuperSearchDisplayItem['kind'] => {
  if (command === SUPER_SEARCH_COMMANDS.START_GUIDED_TOUR) {
    return 'guided-tour';
  }

  return command ? 'command' : 'firm-setting';
};

const toDisplayItem = ({
  command,
  contextLabel,
  context,
  key,
  label,
  path,
  tourId,
  typeLabel,
}: FirmCommandDefinition & { context: SuperSearchContext }): SuperSearchDisplayItem | null => {
  if (!context.firm) {
    return null;
  }

  const result: SuperSearchResult = {
    firm: context.firm,
    id: `firm:${context.firm.id}:command:${key}`,
    label,
    page: {
      key,
      label,
      scope: SuperSearchPageResult.scope.FIRM,
    },
    path: path ?? '',
    search_text: `${label} ${key} ${context.firm.name}`,
    subtitle: contextLabel ?? context.firm.name,
    type: SuperSearchResult.type.FIRM_PAGE,
  };

  return {
    command,
    id: result.id,
    isOutsideContext: false,
    kind: getCommandKind(command),
    label,
    page: result.page,
    result,
    subtitle: result.subtitle,
    tourId,
    typeLabel,
  };
};

const toSection = ({
  context,
  definitions,
  id,
  label,
  query,
}: {
  context: SuperSearchContext;
  definitions: FirmCommandDefinition[];
  id: string;
  label: string;
  query: string;
}): SuperSearchSection | null => {
  const items = definitions.flatMap(definition => {
    if (!matchesSuperSearchQuery(query, `${definition.label} ${definition.key}`)) {
      return [];
    }

    const item = toDisplayItem({ ...definition, context });
    return item ? [item] : [];
  });

  return items.length > 0 ? { id, items, label } : null;
};

export const getFirmCommandSections = ({
  availableTours,
  context,
  hasCompanies,
  routeFirm,
  pendingPage,
  permissions,
  query,
}: {
  availableTours: GuidedTourRegistryItem[];
  context: SuperSearchContext;
  hasCompanies: boolean;
  routeFirm?: SuperSearchFirmResult;
  pendingPage: boolean;
  permissions: FirmCommandPermissions;
  query: string;
}): SuperSearchSection[] => {
  if (!routeFirm || pendingPage) {
    return [];
  }

  const firm = context.firm ?? routeFirm;
  const commandContext = { ...context, firm };
  const hasFirmScope = Boolean(context.firm && !context.company);
  const actions: FirmCommandDefinition[] = [];
  const settings: FirmCommandDefinition[] = [];

  if (hasFirmScope && permissions.canCreateCompany) {
    actions.push({
      command: SUPER_SEARCH_COMMANDS.CREATE_COMPANY,
      key: SUPER_SEARCH_COMMANDS.CREATE_COMPANY,
      label: 'Create Company',
    });
  }

  if (hasFirmScope && permissions.canCreateMeasurementDate && hasCompanies) {
    actions.push({
      command: SUPER_SEARCH_COMMANDS.CREATE_MEASUREMENT_DATE,
      key: SUPER_SEARCH_COMMANDS.CREATE_MEASUREMENT_DATE,
      label: 'Create Measurement Date',
    });
  }

  if (hasFirmScope && permissions.canExportExcel) {
    actions.push({
      command: SUPER_SEARCH_COMMANDS.EXCEL_EXPORT,
      key: SUPER_SEARCH_COMMANDS.EXCEL_EXPORT,
      label: EXCEL_EXPORT,
      path: buildFirmActionPath(firm.slug, FIRM_PAGE_ACTIONS.EXCEL_EXPORT),
    });
  }

  if (hasFirmScope && permissions.canRunBulkActions) {
    actions.push({
      command: SUPER_SEARCH_COMMANDS.BULK_ACTIONS,
      key: SUPER_SEARCH_COMMANDS.BULK_ACTIONS,
      label: BULK_ACTIONS,
      path: buildFirmActionPath(firm.slug, FIRM_PAGE_ACTIONS.BULK_ACTIONS),
    });
  }

  if (hasFirmScope && permissions.canExportPdf) {
    actions.push({
      command: SUPER_SEARCH_COMMANDS.PDF_EXPORT,
      key: SUPER_SEARCH_COMMANDS.PDF_EXPORT,
      label: PDF_EXPORT,
      path: buildFirmActionPath(firm.slug, FIRM_PAGE_ACTIONS.PDF_EXPORT),
    });
  }

  if (hasFirmScope && permissions.canAccessUserManagement) {
    settings.push({
      key: 'user-management',
      label: 'User Management',
      path: firmUserManagementUrl(firm.slug),
    });
  }

  if (hasFirmScope && permissions.canAccessFirmSettings) {
    settings.push(
      { key: 'firm-profile', label: 'Firm Profile', path: firmSettingsUrl(firm.slug) },
      {
        key: 'single-sign-on',
        label: 'Single Sign-On',
        path: firmSingleSignOnUrl(firm.slug),
      },
      { key: 'scim', label: 'SCIM', path: firmScimConfigurationUrl(firm.slug) },
      { key: 'comp-groups', label: 'Comp Groups', path: firmCompGroupsUrl(firm.slug) }
    );
  }

  if (hasFirmScope && permissions.canAccessAuditLogs) {
    settings.push({ key: 'audit-logs', label: 'Audit Logs', path: firmAuditLogsUrl(firm.slug) });
  }

  actions.push({
    command: SUPER_SEARCH_COMMANDS.OPEN_NOTIFICATIONS,
    contextLabel: 'Available anywhere',
    key: SUPER_SEARCH_COMMANDS.OPEN_NOTIFICATIONS,
    label: 'Open Notifications',
    typeLabel: 'Action',
  });
  settings.push({
    contextLabel: 'Your account',
    key: 'account-settings',
    label: 'Account Settings',
    path: userProfileUrl(firm.slug),
    typeLabel: 'Setting',
  });
  const tours: FirmCommandDefinition[] = availableTours.map(({ definition }) => ({
    command: SUPER_SEARCH_COMMANDS.START_GUIDED_TOUR,
    contextLabel: 'Current page',
    key: `guided-tour:${definition.id}`,
    label: `Start ${definition.title} tour`,
    tourId: definition.id,
  }));
  const sections = [
    toSection({
      context: commandContext,
      definitions: actions,
      id: 'firm-actions',
      label: hasFirmScope ? 'Firm actions' : 'Actions',
      query,
    }),
    toSection({
      context: commandContext,
      definitions: settings,
      id: 'firm-settings',
      label: hasFirmScope ? 'Firm settings' : 'Settings',
      query,
    }),
    toSection({
      context: commandContext,
      definitions: tours,
      id: 'guided-tours',
      label: 'Guided tours',
      query,
    }),
  ];

  return sections.filter((section): section is SuperSearchSection => Boolean(section));
};
